import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';
import { useFavorites } from '@/hooks/useFavorites';
import { formatDurationShort, formatRating } from '@/utils/format';

interface MovieCardProps {
  movie: Movie;
  showTags?: boolean;
}

export default function MovieCard({ movie, showTags = false }: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(movie.id);

  return (
    <div className="group relative">
      <Link to={`/detail/${movie.id}`} className="block">
        <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-300/40 bg-background-200">
          <img
            src={movie.poster}
            alt={`${movie.title} 海报`}
            title={`${movie.title} 午夜小电影`}
            className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-transparent to-transparent opacity-80" />
          <span className="absolute left-2 top-2 rounded-md bg-background-950/70 px-2 py-0.5 text-[11px] text-foreground-800 backdrop-blur">
            {movie.category}
          </span>
          <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[11px] text-foreground-900">
            <i className="ri-time-line"></i>
            {formatDurationShort(movie.duration)}
          </span>
          <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-accent-500/90 px-1.5 py-0.5 text-[11px] font-medium text-background-950">
            <i className="ri-star-fill"></i>
            {formatRating(movie.rating)}
          </span>
        </div>
      </Link>

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          toggleFavorite(movie.id);
        }}
        className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md backdrop-blur transition ${
          favorited
            ? 'bg-primary-500 text-white'
            : 'bg-background-950/60 text-foreground-800 hover:text-primary-400'
        }`}
        aria-label={favorited ? '取消收藏' : '收藏'}
      >
        <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
      </button>

      <Link to={`/detail/${movie.id}`} className="mt-3 block">
        <h3 className="truncate text-sm font-medium text-foreground-950 transition group-hover:text-primary-400">
          {movie.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-500">
          {movie.year} · {movie.category}
        </p>
        {showTags && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {movie.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100/80 px-2 py-0.5 text-[11px] text-secondary-900">
                {tag}
              </span>
            ))}
          </div>
        )}
      </Link>
    </div>
  );
}